import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import type { Comment, CreateCommentData, CommentFilters } from '../types/comment';

class CommentService {
  private readonly COLLECTION_NAME = 'comments';

  /**
   * Create a new comment
   */
  async createComment(data: CreateCommentData): Promise<string> {
    try {
      const now = Timestamp.now();

      // Filter out undefined fields (Firestore doesn't allow undefined)
      const commentData: any = {
        targetType: data.targetType,
        targetId: data.targetId,
        userId: data.userId,
        userName: data.userName,
        content: data.content,
        eventId: data.eventId,
        eventTitle: data.eventTitle,
        createdAt: now,
        updatedAt: now,
        likesCount: 0,
        likedBy: []
      };

      // Only add optional fields if they are defined
      if (data.userAvatar) {
        commentData.userAvatar = data.userAvatar;
      }
      if (data.activityId) {
        commentData.activityId = data.activityId;
      }
      if (data.activityType) {
        commentData.activityType = data.activityType;
      }

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), commentData);

      return docRef.id;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw new Error('Failed to create comment');
    }
  }

  /**
   * Get comments for a specific target (activity or event)
   */
  async getComments(
    targetType: 'activity' | 'event',
    targetId: string,
    options: {
      limitCount?: number;
      lastDoc?: DocumentSnapshot;
    } = {}
  ): Promise<{ comments: Comment[]; lastDoc: DocumentSnapshot | undefined; hasMore: boolean }> {
    try {
      const { limitCount = 20, lastDoc } = options;

      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('targetType', '==', targetType),
        where('targetId', '==', targetId),
        orderBy('createdAt', 'desc'),
        limit(limitCount + 1) // Fetch one extra to check if there are more
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const hasMore = snapshot.docs.length > limitCount;
      const docs = hasMore ? snapshot.docs.slice(0, limitCount) : snapshot.docs;

      const comments = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Comment));

      const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : undefined;

      return { comments, lastDoc: newLastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw new Error('Failed to fetch comments');
    }
  }

  /**
   * Get all comments related to an event (both event comments and activity comments)
   */
  async getEventComments(
    eventId: string,
    options: {
      limitCount?: number;
      lastDoc?: DocumentSnapshot;
    } = {}
  ): Promise<{ comments: Comment[]; lastDoc: DocumentSnapshot | undefined; hasMore: boolean }> {
    try {
      const { limitCount = 20, lastDoc } = options;

      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc'),
        limit(limitCount + 1)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const hasMore = snapshot.docs.length > limitCount;
      const docs = hasMore ? snapshot.docs.slice(0, limitCount) : snapshot.docs;

      const comments = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Comment));

      const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : undefined;

      return { comments, lastDoc: newLastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching event comments:', error);
      throw new Error('Failed to fetch event comments');
    }
  }

  /**
   * Get comment count for a target
   */
  async getCommentCount(targetType: 'activity' | 'event', targetId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('targetType', '==', targetType),
        where('targetId', '==', targetId)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting comment count:', error);
      return 0;
    }
  }

  /**
   * Update a comment
   */
  async updateComment(commentId: string, content: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await updateDoc(docRef, {
        content,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      throw new Error('Failed to update comment');
    }
  }

  /**
   * Delete a comment
   */
  async deleteComment(commentId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw new Error('Failed to delete comment');
    }
  }

  /**
   * Toggle like on a comment
   */
  async toggleLike(commentId: string, userId: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, commentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Comment not found');
      }

      const comment = docSnap.data() as Comment;
      const likedBy = comment.likedBy || [];
      const isLiked = likedBy.includes(userId);

      if (isLiked) {
        // Unlike
        await updateDoc(docRef, {
          likedBy: arrayRemove(userId),
          likesCount: increment(-1),
          updatedAt: Timestamp.now()
        });
        return false;
      } else {
        // Like
        await updateDoc(docRef, {
          likedBy: arrayUnion(userId),
          likesCount: increment(1),
          updatedAt: Timestamp.now()
        });
        return true;
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      throw new Error('Failed to toggle like');
    }
  }

  /**
   * Delete all comments for a target (cleanup when activity/event is deleted)
   */
  async deleteCommentsForTarget(targetType: 'activity' | 'event', targetId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('targetType', '==', targetType),
        where('targetId', '==', targetId)
      );

      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting comments:', error);
      throw new Error('Failed to delete comments');
    }
  }
}

export const commentService = new CommentService();
